import { memo, useCallback, useMemo, useState } from 'react'
import { IconInfoCircleFilled } from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import {
  ALGOLIA_PRODUCTS_INDEX_NAME,
  getCurrentAlgoliaInsightsUserToken,
  sendAlgoliaInsightsClickEvent
} from 'lib/algolia'
import { LoginModal } from 'modules/auth'
import { CustomerAgeStatus, DateOfBirthModal } from 'modules/customer'
import {
  getAlgoliaProductID,
  getProductInsightsDataAttributes,
  getProductPrices,
  getProductUnitDetails,
  ProductActionButton,
  ProductTypes,
  useProductAgeGate
} from 'modules/products'
import { SeoListName } from 'shared/analytics'
import {
  getSkeletonImagePlaceholder
} from 'shared/ui/Skeleton/SkeletonImagePlaceholder'
import { KEYBOARD_KEYS } from 'shared/ui/types'
import { addTestId } from 'shared/utils/tests'

import styles from './SearchProductCard.module.scss'

const AddToCartButtonWrapper = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={clsx(styles.addToCartButtonWrapper, className)} />
)

interface SearchProductCardProps {
  product: ProductTypes
  idxOnList: number
  queryID?: string | null // Required for Algolia analytics
  customerId?: string
}

const SearchProductCard = ({
  product,
  idxOnList,
  queryID,
  customerId
}: SearchProductCardProps) => {
  const [isHovering, setIsHovering] = useState(false)
  const handleMouseOver = useCallback(() => setIsHovering(true), [])
  const handleMouseOut = useCallback(() => setIsHovering(false), [])
  const pathname = usePathname()

  // Covers middle-click / ctrl-click "open in new tab", which fires a native `auxclick`
  // that GTM's click-based insights trigger (see data-insights-click-event-trigger) misses.
  const handleAuxClick = useCallback(
    async (e: React.MouseEvent) => {
      if (e.button !== 1 || !queryID) return

      const userToken = await getCurrentAlgoliaInsightsUserToken(customerId)

      sendAlgoliaInsightsClickEvent({
        index: ALGOLIA_PRODUCTS_INDEX_NAME,
        queryID,
        objectID: getAlgoliaProductID(product),
        position: idxOnList + 1,
        userToken
      })
    },
    [queryID, customerId, product, idxOnList]
  )

  const {
    userAgeStatus,
    loginModalOpen,
    dobModalOpen,
    isProductBlurred,
    blurProductMessage,
    handleBlurredProductClick,
    handleCloseLoginModal,
    handleCloseDobModal
  } = useProductAgeGate({ product })

  const { isDiscounted, currentPrice, defaultPrice, unit, lowestPrice30days } =
    useMemo(
      () => ({
        ...getProductPrices(product),
        isDiscounted: product.specialOfferPricing !== null,
        unit: getProductUnitDetails(product),
        isAvailable: product.isAvailable
      }),
      [product]
    )

  const isUserUnderage = userAgeStatus === CustomerAgeStatus.underage

  return (
    <>
      <article
        className={styles.productCardWrapper}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        {...getProductInsightsDataAttributes(queryID, product, idxOnList)}
      >
        <Link
          className={styles.productCardContent}
          tabIndex={isProductBlurred ? -1 : 0}
          href={`/${product.slug}`}
          // Custom attribute to trigger Algolia insights click event in GTM.
          data-insights-click-event-trigger={queryID ? true : undefined}
          onAuxClick={e => void handleAuxClick(e)}
          {...addTestId('search-product-card')}
        >
          <div className={styles.productImage}>
            <Image
              src={product.imageUrl as string}
              alt={product.name}
              width={80}
              height={80}
              placeholder={getSkeletonImagePlaceholder(80, 80)}
              style={{ objectFit: 'contain' }}
            />
          </div>
          <div className={styles.productDetails}>
            <div className={styles.productInfo}>
              <div className={styles.productBrand} {...addTestId('search-product-brand')}>
                {product.nameShort}
              </div>
              <span className={styles.productName} {...addTestId('search-product-name')}>
                {product.nameDescriptionShort}
              </span>
              <div className={styles.productUnit} {...addTestId('search-product-unit')}>
                {unit}
              </div>
            </div>
            <div className={styles.productPriceWrapper}>
              {product.specialOfferPricing?.discount && (
                <mark className={styles.promoPercentTag}>{`-${product.specialOfferPricing?.discount}`}</mark>
              )}
              {isDiscounted ? (
                <ins
                  className={styles.currentPrice}
                  dangerouslySetInnerHTML={{ __html: currentPrice }}
                  {...addTestId('search-product-current-price')}
                />
              ) : (
                <span
                  className={styles.currentPrice}
                  dangerouslySetInnerHTML={{ __html: currentPrice }}
                  {...addTestId('search-product-current-price')}
                />
              )}
              {isDiscounted && (
                <del
                  className={styles.oldPrice}
                  dangerouslySetInnerHTML={{ __html: defaultPrice }} />
              )}
              {isHovering && isDiscounted && (
                <div className={styles.lowestPrice}>
                  <IconInfoCircleFilled
                    style={{ color: 'var(--color-border-strong)' }}
                    size={12}
                  />
                  Najniższa cena z 30 dni przed obniżką:
                  <span
                    dangerouslySetInnerHTML={{
                      __html: lowestPrice30days || ''
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </Link>
        <AddToCartButtonWrapper>
          {!isProductBlurred && (
            <ProductActionButton
              product={product}
              seoButtonPlace={pathname || ''}
              idxOnList={idxOnList}
              listName={SeoListName.SEARCH_LIST_NAME}
              isCollapsible={true}
              algoliaInsightsAttributes={{
                'data-insights-add-to-cart-event-trigger': true
              }}
            />
          )}
        </AddToCartButtonWrapper>
        {isProductBlurred && (
          <div
            className={clsx(styles.blurOverlay, {
              [styles.userUnderage]: isUserUnderage
            })}
            onClick={e => handleBlurredProductClick(e)}
            aria-label='Produkt 18+. Wymaga potwierdzenia wieku.'
            onKeyDown={e =>
              e.key === KEYBOARD_KEYS.ENTER && handleBlurredProductClick(e)
            }
          >
            <Image
              src='/static/UnderEighteen.svg'
              alt='Niedostępny dla osób poniżej 18 lat'
              width={64}
              height={64}
            />
            <button
              className={clsx(styles.blurredInfoWrapper, {
                [styles.userUnderage]: isUserUnderage
              })}
              type='button'
              tabIndex={isUserUnderage ? -1 : 0}
            >
              {blurProductMessage}
            </button>
          </div>
        )}
      </article>
      <LoginModal open={loginModalOpen} onClose={handleCloseLoginModal} />
      <DateOfBirthModal open={dobModalOpen} onClose={handleCloseDobModal} />
    </>
  )
}

export default memo(SearchProductCard)
